/**
 * Hook to sync market statistics from the stats WebSocket channel
 * into the Zustand market stats store.
 * 
 * Called ONCE at app root (AppContent) to establish a single subscription.
 * All components read from the store via selectors -- no per-component subscriptions.
 * 
 * Pattern follows use-mark-price-sync.ts and use-oracle-sync.ts.
 */

import { useEffect, useCallback } from "react";
import { wsClient, type WsStatsMessage, type WsMessage } from "../ws-client";
import { useMarketStatsStore } from "../store/market-stats-store";
import { PerpMarketProgram } from "@/solana/programs/perp-market";
import type { MarketSymbol } from "@/solana/constants";
import { debug } from "@/lib/debug";

const USE_WEBSOCKET = import.meta.env.VITE_USE_WEBSOCKET !== "false";

/**
 * Sync market stats from WebSocket into the Zustand store.
 * Must be called once at app root level.
 */
export const useMarketStatsSync = () => {
  const setStats = useMarketStatsStore.getState().setStats;
  const setConnected = useMarketStatsStore.getState().setConnected;

  const handleStatsMessage = useCallback((message: WsMessage) => {
    if (message.type !== "stats") return;

    const statsMsg = message as WsStatsMessage;

    if (statsMsg.stats && statsMsg.stats.length > 0) {
      // Update the store (single update for all markets)
      setStats(statsMsg.stats);

      // Side effect: Sync prices to PerpMarketProgram for localStorage cache
      // This enables instant display in dropdowns before WebSocket reconnects
      statsMsg.stats.forEach((stat) => {
        const marketPrice = parseFloat(stat.market_price);
        const oraclePrice = parseFloat(stat.oracle_price);
        const openInterestUsd = parseFloat(stat.open_interest_usd);

        const openInterest = marketPrice > 0 ? openInterestUsd / marketPrice : 0;

        PerpMarketProgram.updateMarketStats(stat.market as MarketSymbol, {
          currentPrice: marketPrice,
          oraclePrice: oraclePrice,
          fundingRate: parseFloat(stat.funding_rate),
          nextFundingTime: new Date(stat.next_funding_at).getTime(),
          openInterest: openInterest,
          volume24h: parseFloat(stat.volume_24h_usd),
        });
      });
    }
  }, [setStats]);

  useEffect(() => {
    if (!USE_WEBSOCKET) {
      debug.log("MarketStatsSync", "WebSocket disabled");
      return;
    }

    debug.log("MarketStatsSync", "Subscribing to stats channel (single subscription)");

    // Single WebSocket subscription for all market stats
    const unsubscribe = wsClient.subscribe("stats", handleStatsMessage);

    // Single interval to check connection state
    const checkConnection = () => {
      const state = wsClient.getState("stats");
      setConnected(state === "connected");
    };

    checkConnection();
    const interval = setInterval(checkConnection, 1000);

    return () => {
      debug.log("MarketStatsSync", "Unsubscribing from stats channel");
      unsubscribe();
      clearInterval(interval);
    };
  }, [handleStatsMessage, setConnected]);
};
